import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Heading, LiliButton, LiliButtonType, Text } from '@lili/lili-frontend-common';
import { PlanUpgradeFlow } from '../../planUpgrade/index';
import BalanceUpLottie from '../../../assets/lotties/balance_up.json';
import Lottie from '../../../generic-components/Lottie';
import { ReactComponent as MoneyTransferIcon } from '../../../assets/icons/svg/shared/ic-shared-money-transfer.svg';
import { ReactComponent as CalendarIcon } from '../../../assets/icons/svg/shared/ic-shared-30-calendar.svg';
import { ReactComponent as BankIcon } from '../../../assets/icons/svg/shared/ic-shared-bank-thin.svg';
import { ReactComponent as UpgradeIcon } from '../../../assets/icons/svg/shared/ic-shared-upgrade.svg';
import { featureAccess, LimitedFeatureType } from '../../../services/appAccess.service';
import { FeatureAccess } from '../../../pages/AccountSettings/components/rolesAndPermissions/PermissionsList';

interface BalanceUpEligibilityNoticeProps {
  onSubmit: () => void;
  upgrade?: boolean;
}

const BalanceUpEligibilityNotice = ({ onSubmit, upgrade }: BalanceUpEligibilityNoticeProps) => {
  const { t } = useTranslation('balanceUpTranslation');
  const itemStyle = 'flex flex-col px-[32px] pt-[14px] pb-[24px] items-center max-w-[318px]';
  const [isPlansPopupPresented, setIsPlansPopupPresented] = useState(false);

  const shouldDisablePlanUpgrade = featureAccess(null, LimitedFeatureType.UpgradePlan) !== FeatureAccess.READ_WRITE;
  const shouldDisableAddMoney =
    featureAccess(null, LimitedFeatureType.AddMoneyACHLinkedAccount) !== FeatureAccess.READ_WRITE &&
    featureAccess(null, LimitedFeatureType.AddMoneyACHDirectDeposit) !== FeatureAccess.READ_WRITE;

  return (
    <div className='flex flex-col justify-center items-center h-full w-full px-[40px] pb-[20px]'>
      <Lottie className='w-[154px] h-[95px] my-[40px]' isAutoplay isLoop={false} lottieAsset={BalanceUpLottie} />
      <Heading.H5>{t('NOT_ELIGIBLE.TITLE')}</Heading.H5>
      <Heading.H9>{t('NOT_ELIGIBLE.SUBTITLE')}</Heading.H9>
      <div className='flex mt-[24px]'>
        {upgrade && (
          <div className={itemStyle}>
            <UpgradeIcon className='text-text-main-primary' />
            <Text.H9 className='text-center'>{t('NOT_ELIGIBLE.UNLOCK_BY_UPGRADE')}</Text.H9>
          </div>
        )}
        <div className={itemStyle}>
          <MoneyTransferIcon className='text-text-main-primary' />
          <Text.H9 className='text-center'>{t('NOT_ELIGIBLE.MIN_DEPOSIT')}</Text.H9>
        </div>
        <div className={itemStyle}>
          <CalendarIcon className='text-text-main-primary' />
          <Text.H9 className='text-center'>{t('NOT_ELIGIBLE.MIN_ACTIVE')}</Text.H9>
        </div>
        <div className={itemStyle}>
          <BankIcon className='text-text-main-primary' />
          <Text.H9 className='text-center'>{t('NOT_ELIGIBLE.MIN_HISTORY')}</Text.H9>
        </div>
      </div>
      <div className='mt-[24px]'>
        <Text.H10 className='text-text-main-secondary'>
          {upgrade ? t('NOT_ELIGIBLE.UPGRADE_PLAN') : t('NOT_ELIGIBLE.CTA_ADDITION')}
        </Text.H10>
      </div>
      {upgrade ? (
        <LiliButton
          disabled={shouldDisablePlanUpgrade}
          className='w-[358px] mt-[16px] mb-[40px]'
          type={LiliButtonType.PRIMARY}
          onClick={() => setIsPlansPopupPresented(true)}
          id='planUpgradeCta'
          title={t('NOT_ELIGIBLE.CTA_UPGRADE')}
        />
      ) : (
        <LiliButton
          disabled={shouldDisableAddMoney}
          className='w-[358px] mt-[16px] mb-[40px]'
          type={LiliButtonType.PRIMARY}
          onClick={onSubmit}
          id='enrollCta'
          title={t('NOT_ELIGIBLE.CTA')}
        />
      )}
      {isPlansPopupPresented && (
        <PlanUpgradeFlow
          onCloseClick={() => {
            setIsPlansPopupPresented(false);
          }}
          origin='Plan Promotion'
          promotionType='BalanceUp Feature'
        />
      )}
    </div>
  );
};

export default BalanceUpEligibilityNotice;
